import { useCallback, useMemo } from 'react';

import { ClinicalDataCard } from './ClinicalDataCard';
import { AnxietyRiskToggle } from './fields/AnxietyRiskToggle';
import { HomeworkTextarea } from './fields/HomeworkTextarea';
import { MoodEnergySlider } from './fields/MoodEnergySlider';
import { TechniquesCombobox } from './fields/TechniquesCombobox';

// Presenting concerns moved off the clinical-data JSONB blob and onto Session.concerns
// (categorical enum array). Captured at booking + at the completion dialog. Edit happens
// pre-completion via PATCH /sessions/:id; post-completion they are frozen by the BE.
export type ClinicalDataKey =
  | 'mood'
  | 'energy'
  | 'anxiety_level'
  | 'risk_level'
  | 'techniques_used'
  | 'homework_given'
  | 'next_session_goals';

export type ClinicalDataValue = number | string | string[] | null | undefined;

export interface ClinicalDataPartial {
  mood?: number | null;
  energy?: number | null;
  anxiety_level?: 'low' | 'medium' | 'high' | null;
  risk_level?: 'low' | 'medium' | 'high' | null;
  techniques_used?: string[] | null;
  homework_given?: string | null;
  next_session_goals?: string | null;
  [other: string]: unknown;
}

interface Props {
  clinicalData: ClinicalDataPartial | null;
  onUpdate: (next: ClinicalDataPartial) => void;
  isReadOnly: boolean;
}

const isPopulated = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'number') return true;
  return false;
};

export function ClinicalDataRibbon({ clinicalData, onUpdate, isReadOnly }: Props) {
  const data = useMemo(() => clinicalData ?? {}, [clinicalData]);

  const updateField = useCallback(
    (key: ClinicalDataKey, value: ClinicalDataValue) => {
      const next: ClinicalDataPartial = { ...data, [key]: value };
      onUpdate(next);
    },
    [data, onUpdate],
  );

  const moodEnergyPopulated = [data.mood, data.energy, data.anxiety_level, data.risk_level].filter(
    isPopulated,
  ).length;
  const techniquesPopulated = isPopulated(data.techniques_used) ? 1 : 0;
  const homeworkPopulated = isPopulated(data.homework_given) ? 1 : 0;
  const goalsPopulated = isPopulated(data.next_session_goals) ? 1 : 0;

  return (
    <div className="flex flex-col gap-3">
      <ClinicalDataCard
        cardId="mood-energy"
        title="Mood & energy"
        populatedCount={moodEnergyPopulated}
        totalCount={4}
      >
        <div className="flex flex-col gap-3">
          <MoodEnergySlider
            label="Mood"
            value={data.mood}
            onChange={(v) => {
              updateField('mood', v);
            }}
            disabled={isReadOnly}
          />
          <MoodEnergySlider
            label="Energy"
            value={data.energy}
            onChange={(v) => {
              updateField('energy', v);
            }}
            disabled={isReadOnly}
          />
          <AnxietyRiskToggle
            label="Anxiety"
            value={data.anxiety_level}
            onChange={(v) => {
              updateField('anxiety_level', v);
            }}
            disabled={isReadOnly}
          />
          <AnxietyRiskToggle
            label="Risk"
            value={data.risk_level}
            onChange={(v) => {
              updateField('risk_level', v);
            }}
            disabled={isReadOnly}
          />
        </div>
      </ClinicalDataCard>

      <ClinicalDataCard
        cardId="techniques"
        title="Techniques used"
        populatedCount={techniquesPopulated}
        totalCount={1}
      >
        <TechniquesCombobox
          label=""
          inputAriaLabel="Techniques used"
          value={data.techniques_used}
          onChange={(v) => {
            updateField('techniques_used', v);
          }}
          disabled={isReadOnly}
        />
      </ClinicalDataCard>

      <ClinicalDataCard
        cardId="homework"
        title="Homework given"
        populatedCount={homeworkPopulated}
        totalCount={1}
      >
        <HomeworkTextarea
          label=""
          ariaLabel="Homework given"
          placeholder="What is the client working on between sessions?"
          value={data.homework_given}
          onChange={(v) => {
            updateField('homework_given', v);
          }}
          disabled={isReadOnly}
        />
      </ClinicalDataCard>

      <ClinicalDataCard
        cardId="goals"
        title="Goals for next session"
        populatedCount={goalsPopulated}
        totalCount={1}
      >
        <HomeworkTextarea
          label=""
          ariaLabel="Goals for next session"
          placeholder="What should the client focus on next session?"
          value={data.next_session_goals}
          onChange={(v) => {
            updateField('next_session_goals', v);
          }}
          disabled={isReadOnly}
        />
      </ClinicalDataCard>
    </div>
  );
}
